import React, { useEffect, useState } from 'react'
import { Alert, Card, Col, DatePicker, Empty, Row, Statistic, Table } from 'antd'
import { Bar } from 'react-chartjs-2'
import PageTitle from 'components/PageTitle'
import { getRequest, inventoryUsageUrl } from '../../config'
// Importing registers the chart.js pieces the bar chart needs (the same module the analytics use).
import { barOptions } from '../Analytics/utils'
import HealthStrip from './HealthStrip'
import { formatTokens, formatUsd, spendSeries } from './utils'

const { RangePicker } = DatePicker

// What the assistant is costing. VISIBILITY, not a limit: the bot has no spend cap, because the OpenAI
// account already sends a threshold notification and auto-charges. This page exists so a surprise on
// that notification can be traced to a day and a feature.
//
// Token counts are exact for understanding messages. A web-search MSRP lookup is recorded as one
// call with no tokens, because the library it goes through does not return them — its cost is a
// rough estimate at best.
const UsagePage = () => {
  const [loading, setLoading] = useState(false)
  const [usage, setUsage] = useState(null)
  const [range, setRange] = useState(null)

  useEffect(() => {
    const query = range && range[0] && range[1]
      ? `?from=${range[0].format('YYYY-MM-DD')}&to=${range[1].format('YYYY-MM-DD')}`
      : ''
    getRequest(`${inventoryUsageUrl}${query}`, 'usage', setLoading).then(response => {
      if (response.success) setUsage(response.data)
    })
  }, [range])

  const series = spendSeries(usage && usage.daily)
  const chart = {
    labels: series.labels,
    datasets: [{ label: 'Estimated cost (USD)', data: series.data, backgroundColor: '#1677ff' }]
  }

  /* eslint-disable react/display-name */
  const featureColumns = [
    { title: 'What', dataIndex: 'feature', key: 'feature' },
    { title: 'Calls', dataIndex: 'calls', key: 'calls' },
    { title: 'Input tokens', key: 'in', render: (_, r) => formatTokens(r.inputTokens) },
    { title: 'Output tokens', key: 'out', render: (_, r) => formatTokens(r.outputTokens) },
    { title: 'Cost', key: 'cost', render: (_, r) => formatUsd(r.cost) }
  ]
  const dayColumns = [
    { title: 'Day', dataIndex: 'day', key: 'day' },
    { title: 'Calls', dataIndex: 'calls', key: 'calls' },
    { title: 'Cost', key: 'cost', render: (_, r) => formatUsd(r.cost) }
  ]
  /* eslint-enable react/display-name */

  return (
    <div className="container-fluid">
      <PageTitle title="Inventory assistant · AI usage" />
      <HealthStrip />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="There is no spend cap on the bot."
        description="OpenAI's own threshold notification and auto-charge are the safety net. These figures are estimates from token counts, for tracing a surprise to a day and a feature."
      />
      <Card
        loading={loading && !usage}
        title="Spend"
        extra={<RangePicker value={range} onChange={setRange} />}
      >
        {usage ? (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={12} md={6}>
                <Statistic title="Today" value={formatUsd(usage.today.cost)} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Calls today" value={usage.today.calls} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="In this range" value={formatUsd(usage.totalCost)} />
              </Col>
            </Row>
            {usage.daily.length === 0 ? (
              <Empty description="No AI calls in this range" />
            ) : (
              <div style={{ height: 240, marginBottom: 24 }}>
                <Bar data={chart} options={barOptions} />
              </div>
            )}
            <Row gutter={16}>
              <Col xs={24} lg={14}>
                <Table
                  size="small"
                  title={() => 'By feature'}
                  rowKey="feature"
                  columns={featureColumns}
                  dataSource={usage.byFeature}
                  pagination={false}
                />
              </Col>
              <Col xs={24} lg={10}>
                <Table
                  size="small"
                  title={() => 'By day'}
                  rowKey="day"
                  columns={dayColumns}
                  dataSource={usage.daily}
                  pagination={{ pageSize: 10, size: 'small' }}
                />
              </Col>
            </Row>
          </>
        ) : null}
      </Card>
    </div>
  )
}

export default UsagePage
